import { ControllerInterface } from "csi/action/Controller";
import CsiFormData from "csi/action/FormData";
import JsonResponse from "csi/comm/JsonResponse";
import Key from "csi/core/Key";
import Spec from "csi/core/Spec";
import { Field } from "csi/def/Field";
import enums from "csi/enums";
import locale from "csi/locale";
import { writeFormFields } from "csi/service/writerService";
import { NamesResult, StoreNames } from "csi/store/Names";
import { StoreValues } from "csi/store/Values";
import { DISABLED_PROPERTY, EMPTY_GROUP_ITEM_PROPERTY, IS_GROUP_PROPERTY } from "csi/store/common";
import { NewInterface } from "csi/table/NewInterface";
import { C8Node, cnl } from "csi/typings";
import { sortByString } from "csi/utility/arrayUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import xml from "csi/xml";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import _ from "lodash";

/** the operators to exclude from PP and Custom View Filters */
export const EXCLUDE_OPERATORS_MAP: Record<string, string[]> = {
	boolean: ["GT", "LT", "GE", "LE"],
	ref: ["GT", "LT", "GE", "LE"],
	string: ["GT", "LT", "GE", "LE"],
	time: ["GT", "LT"],
};

/** function for react component to do additional request on save  */
export type ControllerSaveFunction = (promise?: Promise<JsonResponse>) => void;

export interface ComponentFilterItem extends ComponentItem {
	/**count of this item in database */
	count: number;

	/** indicate if need to hide the count */
	hideCount?: boolean;

	/** the cnls of the return nodes per group */
	cnls?: cnl[];
}

export type CnlsAndValues = {
	/** the cnls of the return nodes per group */
	cnls?: cnl[];

	/** the values of the return nodes per group */
	values: any[];
}

export interface ControllerProps {
	/** the action controller */
	actionController?: ControllerInterface;

	/** for using store directly */
	store?: ReactionStore;

	/** handler for cancel */
	onClose?: VoidFunction;

	/** handler for save */
	onSave?: ControllerSaveFunction;
}

export interface ReactViewProps {
	/** the node attached to this react component */
	node?: C8Node;
}

/** interface for attribute spec and filter values */
export interface AttributeSpecAndValues {
	/** the cnls for this attribute */
	cnls?: cnl[];

	/** attribute spec */
	spec: Spec;

	/** the values for this attribute */
	values?: ComponentFilterItem[];
}

/**
 * convert Enum to ComponentItem[]
 */
export function convertEnumToComponentItem(enumStoreName: string, stripEnumListPrefix?: boolean): ComponentItem[] {
	return enums.getStore(enumStoreName, null, null, true)
		.query()
		.map(({ id, name }) => ({
			id: stripEnumListPrefix ? xml.enumGetText(id) : id,
			name,
		}));
}

/**
 * convert Node to ComponentItem
 */
export function convertNodeToComponentItem(node: C8Node): ComponentItem | undefined {
	if (node) {
		return { id: node.$URL, node: node };
	}
}

/**
 * convert Node[] to ComponentItem[]
 */
export function convertNodesToComponentItems(nodes: C8Node[]): ComponentItem[] | undefined {
	return nodes.map((node) => {
		return convertNodeToComponentItem(node);
	});
}

/**
 * convert Spec to ComponentItem
 */
export const convertSpecToComponentItem = (spec: Spec, loc?: ILocaleProvider): ComponentItem => {
	const { key, name } = spec;

	return {
		id: spec.getKey(),
		name,
		// TODO: my case is the only one that passes in the locale provider and get's the extendedPath endType name.
		// Should this be how it is done for all the other cases?
		group: loc ? loc.resource(`${spec.key.extendedPath.endType}.Name`) : locale.type(key.domainType)
			?.toUpperCase(),
	};
};

/**
 * convert NamesResult[] to ComponentItem[]
 */
export const convertNamesResultToComponentItem = (results: NamesResult[]): ComponentItem[] => {
	return results.map(({ $, Name }) => ({
		id: $,
		name: Name,
		res: "NAME_EMPTY",
	}));
};

/** 
 * convert item from c8 store format to mui select item format 
 */
export function convertNamesStoreValueToComponentItem(val: NamesResult, store: StoreNames): ComponentItem | null {
	if (!val) {
		return null;
	}
	const { idProperty = "id", labelProperty = "name", labelProperty2 = "name2", groupProperty } = store;
	return {
		disabled: val[DISABLED_PROPERTY],
		group: val[groupProperty],
		id: val[idProperty],
		invalid: val._invalid,
		isEmptyGroupItem: val[EMPTY_GROUP_ITEM_PROPERTY],
		isGroup: val[IS_GROUP_PROPERTY],
		name: val[labelProperty],
		name2: val[labelProperty2],
		value: val[idProperty],
	};
}

/** 
 * convert data from c8 store format to mui select items format 
 * based on assumption that grouped store items are sorted by groups and group item going first
 */
function flattenGroupedComponentItems(options: ComponentItem[]): ComponentItem[] {
	let groupName: string = "";
	return options.filter((item) => {
		if (item.isGroup) {
			groupName = item.name;
			return false;
		} else if (groupName) {
			item.group = groupName;
		}
		return true;
	});
}

/** 
 * convert data from c8 store format to mui select items format 
 */
export function convertNamesStoreDataToComponentItems(data: NamesResult[] | dojo.store.api.QueryResults<NamesResult>, store: StoreNames): ComponentItem[] {
	return flattenGroupedComponentItems(_.compact(data.map((val) => convertNamesStoreValueToComponentItem(val, store))));
}

/**
 * Convert value store to component items
 */
export const convertValueStoreToComponentFilterItems = async (store: StoreValues, inMatrix: boolean = false): Promise<ComponentFilterItem[]> => {
	const possibleValues = await store.asyncQuery();
	const idSet = new Set();
	const result: ComponentFilterItem[] = [];

	for (const item of possibleValues) {
		const { _text, _count, _refs } = item;
		const value = store.getIdentity(item);
		const id = value?.toString() || "";

		// avoid generating the duplicate items, blank items so far
		if (idSet.has(id)) {
			continue;
		} else {
			idSet.add(id);
		}

		result.push({
			// when the result past the limit, the CNLs won't return anymore
			cnls: _refs || [],
			count: _count,

			// only hide the count of attribute values of matrix node
			hideCount: inMatrix && !id,
			id,
			name: _text || null,
			res: !_text ? "FILTER_BLANKS" : null,
			value,
		});
	}

	return result;
};

/**
 * finalize the new request in React so that the node can be validated on the server side and can be added to the table as well when proceed
 * @param ac controller
 * @param urlNode the url being created
 */
export const finalizeReactNewRequest = (ac: NewInterface, urlNode: cnl): void => {
	const { bValidate, m_formData: fd, m_writer } = ac;

	// update m_urlNode so that the new row(s) can be added to table
	ac.m_urlNode = urlNode;

	// update all fields from form (if any)
	if (fd) {
		// in the case of React, we must use "storeFormDataFieldValue" so that we can be able to get the value of the field for now
		writeFormFields(m_writer, urlNode, fd.getFields(), !!fd.getNode(), Boolean, (f) => fd.getFieldValue(f));
	}

	// call the validation method on the server
	if (bValidate) {
		m_writer.validateNode(urlNode, true);
	}
};

/** populate initial value based on value on field */
export function storeFormDataFieldValue(f: Field, fd: CsiFormData): void {
	const value = f.getValue();

	// track value in FormData initial values and reset control since view is deleted and field no longer exists in DOM
	// TODO: form values will eventually be stored in MobX and won't have to worry about getting values from DOM
	fd.setFieldInitialValue(f.getControlName(), value);
	f.m_control = null;
}

/**
 * remove the duplicated specs, it happens when user selects the spec as the ad-hoc one, then admin selects that particular one as the global one
 */
export function removeDuplicatedSpecs(specs: Spec[]): Spec[] {
	return _.uniqBy(specs, (x) => x.getKey());
}

/** validate the key, see if it's valid or not(get parsed without any errors and exists on end class) */
export function validateKey(key: string): boolean {
	try {
		const keyObj = new Key({ dpkey: key });

		if (keyObj.id) {
			const rca = keyObj.reflectionAttribute;

			if (!rca) {
				// attribute does not exist on class
				return false;
			}
		}
	} catch (ex) {
		return false;
	}

	return true;
}

/** Given an array of ComponentItems, perform grouping, sorting, and flattening on the array and return the result */
export function organizeComponentItems(componentItems: ComponentItem[]): ComponentItem[] {
	// group the attribute by group, do this as per guideline from MUI
	const groupedItems = _.groupBy(componentItems, (item) => item.group);

	// sort the attribute by name per type
	for (const group in groupedItems) {
		sortByString(groupedItems[group], "name");
	}

	// flatten the results
	return _.flatten(_.values(groupedItems));
}